// src/components/FichaMovil.tsx
//
// Horarios, Ubicación y Contacto en tres solapas, para el celular.
//
// POR QUÉ EXISTE
// --------------
// La landing usa `grid-cols-1 lg:grid-cols-3`. En escritorio, Contacto y
// Horarios viven en la columna lateral y se ven sin scrollear. En el celular
// esa columna cae DEBAJO de todos los servicios: Adolfo tiene seis, así que
// sus horarios quedan a media pantalla de distancia y nadie llega.
//
// Las tres responden preguntas previas a reservar —¿queda cerca?, ¿atiende el
// día que puedo?, ¿cómo pregunto?— así que van juntas y arriba.
//
// SOLO EN CELULAR, A PROPÓSITO
// ----------------------------
// En escritorio la barra lateral muestra las tres cosas AL MISMO TIEMPO.
// Meterlas en solapas ahí sería cambiar "ves todo" por "ves un tercio": las
// solapas esconden dos de cada tres paneles, y eso solo se justifica cuando
// la alternativa es que el contenido no se vea nunca.
//
// ARRANCA EN HORARIOS
// -------------------
// Es la pregunta que hace que la gente se vaya ("¿atiende sábados?"), y desde
// el cambio multi-sede ya trae la sede y la ciudad de cada día, así que
// contesta buena parte de "¿dónde?" también.

import React, { useState } from 'react';
import { Clock, MapPin, Phone, ExternalLink, MessageCircle } from 'lucide-react';
import HorariosMultiSede from './HorariosMultiSede';

type Solapa = 'horarios' | 'ubicacion' | 'contacto';

interface Sede {
  id: number;
  name: string;
  address?: string;
  city?: string;
  phone?: string;
  is_active?: boolean;
}

interface Props {
  professionalId: number;
  nombre?: string;
  telefono?: string;
  businessAddress?: string;
  sedes: Sede[];
  maxAdvanceBooking?: number;
}

const mapsUrl = (texto: string) =>
  `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(texto)}`;

const soloDigitos = (t: string) => t.replace(/\D/g, '');

const FichaMovil: React.FC<Props> = ({
  professionalId,
  nombre,
  telefono,
  businessAddress,
  sedes,
  maxAdvanceBooking,
}) => {
  const [activa, setActiva] = useState<Solapa>('horarios');

  const activas = sedes.filter((s) => s.is_active !== false);

  const solapas: { id: Solapa; etiqueta: string; icono: React.ReactNode }[] = [
    { id: 'horarios', etiqueta: 'Horarios', icono: <Clock className="w-4 h-4" /> },
    { id: 'ubicacion', etiqueta: 'Ubicación', icono: <MapPin className="w-4 h-4" /> },
    { id: 'contacto', etiqueta: 'Contacto', icono: <Phone className="w-4 h-4" /> },
  ];

  // Para el botón de WhatsApp: si hay una sola sede con teléfono propio, ese
  // es el número que atiende; si hay varias, no se puede adivinar cuál, así
  // que se usa el del profesional.
  const sedesConTel = activas.filter((s) => s.phone);
  const telWhatsapp = sedesConTel.length === 1 ? sedesConTel[0].phone : telefono;

  return (
    // La entrada es la misma del panel principal: fade y deslizado desde el
    // costado. El retraso la deja caer justo después de la tarjeta del
    // profesional, para que se lea como una secuencia y no como dos cosas
    // apareciendo a la vez.
    <div
      className="lg:hidden bg-white border border-gray-200 rounded-2xl overflow-hidden text-left animate-slide-in-right"
      style={{ animationDelay: '220ms' }}
    >
      <div className="flex border-b border-gray-200" role="tablist">
        {solapas.map((s) => (
          <button
            key={s.id}
            role="tab"
            aria-selected={activa === s.id}
            onClick={() => setActiva(s.id)}
            className={`flex-1 flex items-center justify-center gap-1.5 py-3 text-[13px] font-semibold transition-colors ${
              activa === s.id
                ? 'text-[#0B7C87] border-b-2 border-[#0FA3B1] bg-[#0FA3B1]/5'
                : 'text-gray-500 border-b-2 border-transparent'
            }`}
          >
            {s.icono}
            {s.etiqueta}
          </button>
        ))}
      </div>

      {/* `key` con la solapa activa para que React lo reemplace y la animación
          vuelva a correr en cada cambio. Sin key, el div es el mismo nodo y el
          navegador no reinicia nada. */}
      <div key={activa} className="p-4 animate-panel-in">
        {activa === 'horarios' && (
          <HorariosMultiSede
            professionalId={professionalId}
            sedes={activas}
            maxAdvanceBooking={maxAdvanceBooking}
            compacto
          />
        )}

        {activa === 'ubicacion' && (
          <div className="space-y-3">
            {activas.length > 0 ? (
              activas.map((s) => {
                const texto = [s.address, s.city].filter(Boolean).join(', ');
                return (
                  <div key={s.id}>
                    <div className="font-medium text-gray-900 text-[15px]">{s.name}</div>
                    {s.city && <div className="text-gray-500 text-[13px]">{s.city}</div>}
                    {s.address && (
                      <a
                        href={mapsUrl(texto)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 mt-1 text-[13px] text-[#0B7C87] font-medium"
                      >
                        {s.address}
                        <ExternalLink className="w-3 h-3 flex-shrink-0" />
                      </a>
                    )}
                  </div>
                );
              })
            ) : businessAddress ? (
              <a
                href={mapsUrl(businessAddress)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-[14px] text-[#0B7C87] font-medium"
              >
                {businessAddress}
                <ExternalLink className="w-3 h-3 flex-shrink-0" />
              </a>
            ) : (
              <p className="text-gray-500 text-[14px]">Sin dirección cargada.</p>
            )}
          </div>
        )}

        {activa === 'contacto' && (
          <div className="space-y-3">
            {telefono && (
              <a
                href={`tel:${soloDigitos(telefono)}`}
                className="flex items-center gap-2 text-[15px] text-gray-800"
              >
                <Phone className="w-4 h-4 text-gray-400 flex-shrink-0" />
                {telefono}
              </a>
            )}

            {sedesConTel.map((s) => (
              <a
                key={s.id}
                href={`https://wa.me/${soloDigitos(s.phone || '')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-start gap-2 text-[14px]"
              >
                <MessageCircle className="w-4 h-4 text-[#25D366] flex-shrink-0 mt-0.5" />
                <span>
                  <span className="text-gray-800">{s.phone}</span>
                  <span className="block text-[12px] text-gray-500">{s.name}</span>
                </span>
              </a>
            ))}

            {telWhatsapp && (
              <a
                href={`https://wa.me/${soloDigitos(telWhatsapp)}?text=${encodeURIComponent(
                  `Hola ${nombre || ''}, vi tu perfil en ConsultAR y me interesa agendar una cita. ¿Podrías darme más información?`
                )}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full bg-[#25D366] text-white py-3 rounded-xl font-semibold text-[15px] active:scale-[0.98] transition-transform"
              >
                <MessageCircle className="w-4 h-4" />
                Contactar por WhatsApp
              </a>
            )}

            {!telefono && sedesConTel.length === 0 && (
              <p className="text-gray-500 text-[14px]">Sin teléfono cargado.</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default FichaMovil;
